import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { lighten, makeStyles } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import HomeIcon from '@material-ui/icons/Home';
import DeleteConfirmDialog from 'adminForm/DeleteConfirmDialog';
import { AppPath } from 'AppRoutes';
import clsx from 'clsx';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row } from 'react-table';
import { AdminDialogType } from './Admin';
import { LogoutBtn } from './LogoutBtn';
import { AdminBox } from './useAdminBoxes';

interface Props {
  selectedFlatRows: Row<AdminBox>[];
  toggleAllRowsSelected: (value?: boolean) => void;
  gotoPage: (updater: number | ((pageIndex: number) => number)) => void;
  openDialog: (type: AdminDialogType) => void;
}

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === 'light'
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
    flex: '1 1 100%',
  },
}));

const AdminTableToolbar = ({
  selectedFlatRows,
  toggleAllRowsSelected,
  gotoPage,
  openDialog,
}: Props) => {
  const numSelected = selectedFlatRows.length;
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const classes = useToolbarStyles();

  const handleDeleteDialogClose = () => setIsDeleteDialogOpen(false);

  const handleClear = useCallback(() => toggleAllRowsSelected(false), [
    toggleAllRowsSelected,
  ]);

  const onDeleteBtnClick = () => setIsDeleteDialogOpen(true);
  const onDeleteSuccess = useCallback(() => {
    setIsDeleteDialogOpen(false);
    gotoPage(0);
    handleClear();
  }, [gotoPage, handleClear]);

  const navigate = useNavigate();
  const handleHomeClick = () => navigate(AppPath.Home);

  const handleAddClick = () => openDialog('create');

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: numSelected > 0,
      })}
    >
      <Tooltip title="ホーム">
        <IconButton onClick={handleHomeClick}>
          <HomeIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="追加">
        <IconButton aria-label="add" onClick={handleAddClick}>
          <AddIcon />
        </IconButton>
      </Tooltip>

      {numSelected > 0 ? (
        <Typography
          className={classes.title}
          color="inherit"
          variant="subtitle1"
        >
          {numSelected} 選択中
        </Typography>
      ) : (
        <Typography className={classes.title} variant="h6" id="tableTitle">
          ボックス
        </Typography>
      )}
      <DeleteConfirmDialog
        isOpen={isDeleteDialogOpen}
        boxIds={selectedFlatRows.map((r) => r.original.id)}
        onDeleteSuccess={onDeleteSuccess}
        onCancelDelete={handleDeleteDialogClose}
      />

      {numSelected > 0 ? (
        <>
          <Tooltip title="クリア">
            <Button
              aria-label="delete"
              variant="outlined"
              size="small"
              onClick={handleClear}
            >
              クリア
            </Button>
          </Tooltip>
          <Tooltip title="削除">
            <IconButton aria-label="delete" onClick={onDeleteBtnClick}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <LogoutBtn />
      )}
    </Toolbar>
  );
};

export default AdminTableToolbar;
